import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subject, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AlertService, AuthService, LanguageService } from '@shared/services';
import { TranslateService } from '@ngx-translate/core';

import {
  IResPay,
  IReqPay,
  IResTransactionInfo,
  IReqPaymentMethodDurationType,
  IResPaymentMethodDurationType,
  IResBanks,
  IReqMobilePayAvailability,
  IResMobilePayAvailability,
  IResQrBankLimits,
} from './general.interface';

@Injectable({
  providedIn: 'root',
})
export class GeneralService {
  transactionInfo$ = new Subject<any>();

  constructor(
    @Inject('systemConfig') public SystemConfig,
    private router: Router,
    protected http: HttpClient,
    public alertService: AlertService,
    private authService: AuthService,
    public translate: TranslateService,
    public languageService: LanguageService,
  ) {}

  // Initialize Transaction
  initialize(route) {
    this.authService.flush();
    if (route !== '') {
      if (!route.snapshot.queryParams['token']) {
        this.router.navigate([404]);
        return;
      }
      const helper = new JwtHelperService();
      const decodedToken = helper.decodeToken(
        route.snapshot.queryParams['token'],
      );

      const auth = {
        token: route.snapshot.queryParams['token'],
        successRedirectUrl: route.snapshot.queryParams['successRedirectUrl'],
        failRedirectUrl: route.snapshot.queryParams['failRedirectUrl'],
        roles: decodedToken.roles,
        transactionId: decodedToken.guid,
        merchantGuid: decodedToken.sub,
      };

      this.authService.auth = auth;
      return true;
    } else {
      return false;
    }
  }

  preInitialize(token: string) {
    const helper = new JwtHelperService();
    const decodedToken = helper.decodeToken(token);

    const initData = {
      amount: decodedToken.amount,
      currency: decodedToken.currency,
      paymentMethods: decodedToken.paymentMethods,
    };

    const auth = {
      token,
      roles: decodedToken.roles,
      ...initData,
    };

    this.authService.auth = auth;
    localStorage.setItem('transaction', JSON.stringify(initData));

    return auth;
  }

  getPaymentMethods() {
    // get payment methods
    const paymentMethodsUrl = '/api/v2/paymentMethods/available';
    const paymentMethods = this.http.post(paymentMethodsUrl, {});

    // get voucher methods
    const merchantGuid = this.authService.auth['merchantGuid'];

    // concat both methods
    return forkJoin([paymentMethods]).pipe(
      map((response) => {
        const pm = response[0]['paymentMethods'];
        // sort payment methods alphabetically and return
        return pm.sort(this.dynamicSort('name'));
      }),
    );
  }

  getTransactionInfo(request?: any): Observable<any> {
    const geolocationUrl = '/api/v2/geolocation';
    const geoLocationResponse = this.http.get<any>(geolocationUrl);

    const trnInfoUrl = '/api/v2/checkout/transaction-info';
    const trnInfoResponse = this.http.post<IResTransactionInfo>(
      trnInfoUrl,
      request,
    );

    return forkJoin([geoLocationResponse, trnInfoResponse]).pipe(
      map((response) => {
        if (response[1].iframe && response[1].iframe === 'true') {
          if (window.self !== window.top) {
            const currLocation = window.location.href;
            this.router.navigate(['iframe-notfound'], {
              queryParams: { currLocation },
              skipLocationChange: true,
            });
            return;
          }
        }
        const trx = {
          ...response[1],
          ip:
            response[0] && response[0].ipAddress
              ? response[0].ipAddress
              : '210.10.10.10',
        };

        if (response[1]['code'] === '00000') {
          localStorage.setItem('transaction', JSON.stringify(trx));
          this.transactionInfo$.next(trx);
          const resLang = response[1].language.toLowerCase();
          const lang = resLang ? resLang : 'en';
          this.languageService.language = lang;

          return trx;
        } else {
          throw new HttpErrorResponse({
            error: response,
            url: trnInfoUrl,
          });
        }
      }),
    );
  }

  // Pay
  pay(request: IReqPay): Observable<IResPay> {
    const url = '/api/v2/checkout/pay';

    return this.http.post<IResPay>(url, request).pipe(
      map((response) => {
        response['paymentMethod'] = request.paymentMethod;
        localStorage.setItem('payment-result', JSON.stringify(response));
        // If response DECLINED and there is no transaction id: this is a warning show with alert
        if (response.status === 'DECLINED' && !response.transactionId) {
          this.alertService.setNotice(response.message, 'warning');
        } else if (
          // if response status equal to ERROR
          response.status === 'ERROR' ||
          // if response status DECLINED and there is transaction id
          (response.status === 'DECLINED' && response.transactionId) ||
          // If response status is APPROVED or WAITING
          // and there is a transaction ID with no redirect URL,
          // navigate to payment-result page to display success/failure directly.
          // For CARD, CREDIT_CARD and CC_LIVE payment methods, skip the extra field check.
          // For other payment methods (e.g., bank transfer, mobile banking, redeem voucher),
          // only navigate if the extra field is missing or empty.
          ((response.status === 'APPROVED' || response.status === 'WAITING') &&
            response.transactionId &&
            !response.redirectUrl &&
            ([
              'CARD',
              'CREDIT_CARD',
              'CC_LIVE',
              'APP_PAY',
              'APPLE_PAY',
              'GOOGLE_PAY',
              'CREDIT_CARD_VIP',
            ].includes(request.paymentMethod) ||
              !response.extra ||
              Object.keys(response.extra).length === 0))
        ) {
          this.router.navigate(['payment-result']);
          return response;
        } else {
          return response;
        }
      }),
    );
  }

  getPaymentMethodDurationType(
    request: IReqPaymentMethodDurationType,
  ): Observable<IResPaymentMethodDurationType> {
    const url = '/api/v2/checkout/getPaymentMethodDurationType';
    return this.http.post<IResPaymentMethodDurationType>(url, request).pipe(
      map((response) => {
        if (!response['message']) {
          return response;
        }
      }),
    );
  }

  getBanks(paymentMethod: string, request?: any): Observable<IResBanks> {
    const url = `/api/v2/checkout/banks/${paymentMethod}`;
    return this.http.post<IResBanks>(url, request).pipe(
      map((response) => {
        if (
          (paymentMethod === 'manualBankTransfer' ||
            paymentMethod === 'otobanktransfer') &&
          response.banks
        ) {
          response.banks = response.banks.sort(this.dynamicSort('bankName'));
          return response;
        }
        if (response.code === '00000') {
          if (response.banks?.length) {
            response.banks = response.banks.sort(this.dynamicSort('name'));
          }
          return response;
        } else {
          throw new HttpErrorResponse({
            error: response,
            url,
          });
        }
      }),
    );
  }

  getBanksByMerchant(): Observable<IResQrBankLimits[]> {
    const url = `/api/v2/instantQr/bankLimits/checkout/get`;
    return this.http.get<IResQrBankLimits[]>(url);
  }

  getBtVoucherBanks(): Observable<IResBanks> {
    const url = `/api/v2/checkout/banks/btvoucher`;
    return this.http.get<IResBanks>(url);
  }

  // ex. sort banks by name
  dynamicSort(property) {
    let sortOrder = 1;

    if (property[0] === '-') {
      sortOrder = -1;
      property = property.substr(1);
    }

    return function (a, b) {
      if (sortOrder === -1) {
        return b[property].localeCompare(a[property]);
      } else {
        return a[property].localeCompare(b[property]);
      }
    };
  }

  backToMerchant(status?: string, newTab?: boolean) {
    let redirectUrl = '';
    if (status === 'fail' || status === 'error' || status === 'declined') {
      redirectUrl = this.authService.auth.failRedirectUrl || '';
    } else {
      redirectUrl = this.authService.auth.successRedirectUrl || '';
    }

    if (!redirectUrl) {
      return;
    }

    if (newTab) {
      window.open(redirectUrl, '_blank').focus();
    } else {
      window.location.href = redirectUrl;
    }
  }

  initializeTransaction(request: any): Observable<any> {
    const url = '/api/v2/checkout/initialize';
    return this.http.post<any>(url, request).pipe(
      map((response) => {
        if (response['code'] === '00000') {
          return response;
        } else {
          throw new HttpErrorResponse({
            error: response,
            url,
          });
        }
      }),
    );
  }

  getPayByLinkUrl(id: string): Observable<any> {
    const url = `/api/v2/getPayByLinkUrl?id=${id}`;
    return this.http.get<any>(url).pipe(
      map((response) => {
        if (!response['message']) {
          return response;
        } else {
          throw new HttpErrorResponse({
            error: response,
            url,
          });
        }
      }),
    );
  }

  checkMobilePayAvailability(
    request: IReqMobilePayAvailability,
  ): Observable<IResMobilePayAvailability> {
    const url = '/api/v2/checkout/hasMobilepayCheckout';
    return this.http.post<IResMobilePayAvailability>(url, request).pipe(
      map((response) => {
        if (!response['message']) {
          return response;
        } else {
          throw new HttpErrorResponse({
            error: response,
            url,
          });
        }
      }),
    );
  }

  validateApplePayMerchant(request: any): Observable<any> {
    const url = '/api/v2/checkout/validateMobilePaySession';
    return this.http.post<any>(url, request).pipe(
      map((response) => {
        if (!response['message']) {
          return response;
        } else {
          throw new HttpErrorResponse({
            error: response,
            url,
          });
        }
      }),
    );
  }
}
